import { useEffect, useState, type ReactElement } from "react";
import { Save, Unlink } from "lucide-react";
import { Badge, Button, FormField, Stack, Surface, Text } from "@ux/primitives";
import { BottomActionBar, Toolbar, UserPicker, WorkspacePicker } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { palette, space } from "@ux/tokens";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { formatActor } from "../common/formatting";
import { UserName } from "../users/UserName";
import { WorkspaceName } from "../workspaces/WorkspaceName";
import type { UserDirectoryEntry } from "../users/types";
import type { WorkspaceView } from "../workspaces/types";
import type {
    WorkspaceAssignmentCreatePayload,
    WorkspaceAssignmentView,
} from "./types";

export type WorkspaceAssignmentFormMode =
    | { kind: "create" }
    | { kind: "view"; record: WorkspaceAssignmentView };

export interface WorkspaceAssignmentFormProps {
    readonly mode: WorkspaceAssignmentFormMode;
    readonly openAssignmentsByWorkspace: ReadonlyMap<string, WorkspaceAssignmentView>;
    readonly onCreate: (payload: WorkspaceAssignmentCreatePayload) => void;
    readonly onRelease: (record: WorkspaceAssignmentView) => void;
    readonly onCancel: () => void;
    readonly isSaving: boolean;
    readonly errorMessage: string | null;
}

interface CreateState {
    readonly id: string;
    readonly workspace: WorkspaceView | null;
    readonly user: UserDirectoryEntry | null;
}

export function WorkspaceAssignmentForm({
    mode,
    openAssignmentsByWorkspace,
    onCreate,
    onRelease,
    onCancel,
    isSaving,
    errorMessage,
}: WorkspaceAssignmentFormProps): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const [state, setState] = useState<CreateState>(() => initialCreateState());
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        if (mode.kind === "create") {
            setState(initialCreateState());
        }
        setValidationError(null);
    }, [mode]);

    const incumbent =
        state.workspace !== null
            ? openAssignmentsByWorkspace.get(state.workspace.id) ?? null
            : null;

    if (mode.kind === "view") {
        return (
            <AssignmentDetail
                record={mode.record}
                onRelease={onRelease}
                onCancel={onCancel}
                isSaving={isSaving}
            />
        );
    }

    function handleSubmit(): void {
        if (state.workspace === null) {
            setValidationError("Pick a workspace.");
            return;
        }
        if (state.user === null) {
            setValidationError("Pick a user.");
            return;
        }

        const payload: WorkspaceAssignmentCreatePayload = {
            id: state.id,
            workspaceId: state.workspace.id,
            userId: state.user.id,
        };

        setValidationError(null);
        onCreate(payload);
    }

    const createActionButtons = (
        <>
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Cancel
            </Button>
            {canEdit ? (
                <Button
                    variant="primary"
                    leadingIcon={<Save size={16} />}
                    onClick={handleSubmit}
                    disabled={isSaving}
                >
                    {isSaving ? "Saving…" : "Assign"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={<Text role="heading">New occupant</Text>}
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {createActionButtons}
                        </Stack>
                    )
                }
            />

            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={4} style={{ maxWidth: 640 }}>
                    {validationError !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {validationError}
                            </Text>
                        </Surface>
                    ) : null}
                    {errorMessage !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Text role="muted" color={palette.danger}>
                                {errorMessage}
                            </Text>
                        </Surface>
                    ) : null}

                    <FormField
                        label="Workspace"
                        required
                        hint="Type to search by name or friendly name. Occupied workspaces are marked. Re-assigning implicitly releases the prior occupant."
                    >
                        {(id) => (
                            <WorkspacePicker
                                id={id}
                                value={state.workspace}
                                onChange={(workspace) => setState((p) => ({ ...p, workspace }))}
                                showOccupiedHint
                            />
                        )}
                    </FormField>

                    {incumbent !== null ? (
                        <Surface tone="raised" padding={3}>
                            <Stack gap={1}>
                                <Text role="eyebrow">Heads up</Text>
                                <Text role="muted">
                                    {`This workspace is currently assigned to `}
                                    <Text as="span" role="body" weight="semibold">
                                        <UserName id={incumbent.userId} />
                                    </Text>
                                    {`. Saving will release that assignment and create a new one.`}
                                </Text>
                            </Stack>
                        </Surface>
                    ) : null}

                    <FormField
                        label="User"
                        required
                        hint="Search the Galileo HR directory by name or email."
                    >
                        {(id) => (
                            <UserPicker
                                id={id}
                                value={state.user}
                                onChange={(user) => setState((p) => ({ ...p, user }))}
                            />
                        )}
                    </FormField>
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{createActionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

interface AssignmentDetailProps {
    readonly record: WorkspaceAssignmentView;
    readonly onRelease: (record: WorkspaceAssignmentView) => void;
    readonly onCancel: () => void;
    readonly isSaving: boolean;
}

function AssignmentDetail({
    record,
    onRelease,
    onCancel,
    isSaving,
}: AssignmentDetailProps): ReactElement {
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();

    const detailActionButtons = (
        <>
            <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Close
            </Button>
            {canEdit && record.isOpen ? (
                <Button
                    variant="danger"
                    leadingIcon={<Unlink size={16} />}
                    onClick={() => onRelease(record)}
                    disabled={isSaving}
                >
                    {isSaving ? "Releasing…" : "Release"}
                </Button>
            ) : null}
        </>
    );

    return (
        <Stack direction="column" gap={0} grow>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Occupant detail</Text>
                        {record.isOpen ? (
                            <Badge tone="accent">Open</Badge>
                        ) : (
                            <Badge tone="neutral">Released</Badge>
                        )}
                    </Stack>
                }
                trailing={
                    isPhone ? null : (
                        <Stack direction="row" gap={2} wrap>
                            {detailActionButtons}
                        </Stack>
                    )
                }
            />
            <div style={{ flex: 1, overflow: "auto", padding: space[5] }}>
                <Stack gap={3} style={{ maxWidth: 640 }}>
                    <DetailRow label="Assignment id" value={record.id} mono />
                    <Stack gap={1}>
                        <Text role="eyebrow">User</Text>
                        <Text role="body">
                            <UserName id={record.userId} />
                        </Text>
                    </Stack>
                    <DetailRow label="User ID" value={record.userId} mono />
                    <Stack gap={1}>
                        <Text role="eyebrow">Workspace</Text>
                        <Text role="body">
                            <WorkspaceName id={record.workspaceId} />
                        </Text>
                    </Stack>
                    <DetailRow label="Workspace ID" value={record.workspaceId} mono />
                    <Surface tone="raised" padding={4}>
                        <Stack gap={2}>
                            <Text role="eyebrow">Audit</Text>
                            <Text role="muted">Assigned — {formatActor(record.assigned)}</Text>
                            <Text role="muted">
                                Released —{" "}
                                {record.released !== undefined && record.released !== null
                                    ? formatActor(record.released)
                                    : "—"}
                            </Text>
                        </Stack>
                    </Surface>
                </Stack>
            </div>

            {isPhone ? <BottomActionBar>{detailActionButtons}</BottomActionBar> : null}
        </Stack>
    );
}

interface DetailRowProps {
    readonly label: string;
    readonly value: string;
    readonly mono?: boolean;
}

function DetailRow({ label, value, mono = false }: DetailRowProps): ReactElement {
    return (
        <Stack gap={1}>
            <Text role="eyebrow">{label}</Text>
            <Text role={mono ? "mono" : "body"}>{value}</Text>
        </Stack>
    );
}

function initialCreateState(): CreateState {
    return {
        id: crypto.randomUUID(),
        workspace: null,
        user: null,
    };
}
